import { CheckOutlined, LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceConfirmReturnEndpoint,
  useStocksServiceGetReturnEndpoint,
  useStocksServiceGetReturnEndpointKey
} from 'src/api/queries'
import {
  InventoryDocumentState,
  Permissions,
  ReturnApprovalStatus
} from 'src/api/requests'
import { LoadingScreen, PageBreadcrumbs, SaveConfirm } from 'src/components'
import { ReturnDetails } from 'src/features/stock/components'
import { useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { toDayjs } from 'src/utils'

export const StockReturnsDetailsPage = () => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const printableRef = useRef<HTMLDivElement>(null)

  const { hasPermission } = useUser()
  const { data, isLoading } = useStocksServiceGetReturnEndpoint(
    { id: String(id) },
    [id],
    {
      enabled: !!id
    }
  )

  const onBack = () => {
    navigate(paths.stock.returns.root)
  }

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Return_${id}`
  })

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      form.setFieldsValue({
        invoiceDate: toDayjs(data?.documentDate),
        invoiceNumber: data?.documentNumber,
        contractId: `${data?.beneficiary?.name} (${t('organizational_structure.idno')}: ${data?.beneficiary?.idno}, ${t('counterparts.contract_number')}: ${data?.contract?.id})`,
        supplier: data?.supplier?.name,
        purchase_center: data?.beneficiary?.name,
        takeoverDate: toDayjs(data?.takeoverDate),
        transporterId: data?.transporter?.id ?? '-',
        transporterAccompanyingNotice: data?.accompanyingNotice ?? '-',
        comment: data?.comment,
        reasons: data?.reasons?.map(
          (reason) => reason?.translation?.[currentLang?.value]
        ),
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.internationalName?.name} ${item?.doses}`,
          productName: item?.product?.name,
          producerName: item?.producer?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          quantity: `${item?.quantity}`,
          unitPrice: item?.unitPrice,
          priceTotal: Number(item?.quantity) * Number(item?.unitPrice)
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, id])

  const { mutate: confirmReturn } = useStocksServiceConfirmReturnEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetReturnEndpointKey, String(id)]
      })
    }
  })

  const onConfirm = (status: ReturnApprovalStatus) => {
    confirmReturn({
      id: String(id),
      requestBody: {
        approvalStatus: status
      }
    })
  }

  const canConfirm =
    hasPermission(Permissions.STOCKS_STOCK_CONFIRM_RETURNS) &&
    (data?.state === InventoryDocumentState.NEW ||
      data?.state === InventoryDocumentState.WAITING)

  const canAccept =
    hasPermission(Permissions.STOCKS_STOCK_ACCEPT_RETURNS) &&
    data?.state === InventoryDocumentState.APPROVED

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock_returns.stock_return')}: ${data?.code}`}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.returns'), path: paths.stock.returns.root },
        { title: `${t('stock_returns.stock_return')}: ${data?.code}` }
      ]}
    >
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBack}>
            {t('common.back')}
          </Button>

          <Flex gap={8}>
            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            {canConfirm && (
              <>
                <SaveConfirm
                  title={`${t('common.confirm')}?`}
                  placement="leftBottom"
                  onConfirm={() => onConfirm(ReturnApprovalStatus.APPROVE)}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    {t('common.confirm')}
                  </Button>
                </SaveConfirm>

                <SaveConfirm
                  title={`${t('stock_administration.reject')}?`}
                  placement="leftBottom"
                  onConfirm={() => onConfirm(ReturnApprovalStatus.REJECT)}
                >
                  <Button danger icon={<CheckOutlined />}>
                    {t('stock_administration.reject')}
                  </Button>
                </SaveConfirm>
              </>
            )}

            {canAccept && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => onConfirm(ReturnApprovalStatus.ACCEPT)}
              >
                {t('common.accept')}
              </Button>
            )}
          </Flex>
        </Flex>

        <div ref={printableRef}>
          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            disabled={!!data}
          >
            <ReturnDetails item={data} form={form} />
          </Form>
        </div>
      </Flex>
    </PageBreadcrumbs>
  )
}
